/**
 * mintWalletSaveLinks — RPC contract for the mintWalletSaveLinks callable.
 * A Google Wallet save link is minted on demand, when the buyer's page
 * renders it, never stored: the processor that inserted the passes writes
 * only `artifacts.walletGoogle = { state: 'ready' }` on OrderAccess
 * (order-access.entity.ts) — no URL anywhere. This callable is what turns
 * that readiness into real links, signed fresh on every call.
 *
 * The forcing reason is the signing key's own lifetime. The save-link JWT is
 * signed with the service account's system-managed key, which Google
 * documents as valid "at least 12 hours" — a floor, not a promise, with no
 * published schedule for when the public half stops resolving. A link
 * persisted at fulfillment and clicked days later bets on that key still
 * being resolvable; a link signed while the buyer is looking at the page is
 * seconds old and cannot lose that bet.
 *
 * The capability is the token, not a session. OrderAccess is already
 * reached by possession of `orderAccessToken` alone — `get` allowed, `list`
 * denied, no client write path — because a buyer has no account to
 * authenticate with. A link minted from that same token grants nothing its
 * holder cannot already read: this callable widens no access OrderAccess
 * doesn't already grant.
 *
 * Fields justified:
 *   orderAccessToken — the OrderAccess capability token; see the module
 *                     docstring above.
 *
 * Output fields justified:
 *   urls — the order's Google Wallet save links, chunked to at most six
 *          passes per link.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const MintWalletSaveLinksInput = z
	.object({
		orgId: Id,
		orderAccessToken: z
			.string()
			.min(1)
			.describe(
				"the OrderAccess capability token — possession alone authorizes this call, the same way it authorizes reading OrderAccess itself",
			),
	})
	.strict();
export type MintWalletSaveLinksInput = z.infer<typeof MintWalletSaveLinksInput>;

export const MintWalletSaveLinksOutput = z
	.object({
		urls: z
			.array(z.string().url())
			.describe(
				"this order's Google Wallet save links, signed fresh for this call — chunked to at most six passes per link so each stays under the Wallet API's safe encoded-JWT length",
			),
	})
	.strict();
export type MintWalletSaveLinksOutput = z.infer<
	typeof MintWalletSaveLinksOutput
>;
